import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { User } from "../../../../server/entities/user.entity";
import {
  BaseSecurityUserHasPermissionRequest
} from "../../../../server/services/base/security/userHasPermission.contract";
import { userPermissions } from "../../../../server/entities/user-role.entity";
import { SharedDataService } from '../shared-data/shared-data.service';

@Injectable({
  providedIn: 'root'
})
export class WebAuthenticationService implements CanActivate {

  user$: BehaviorSubject<User | undefined> = new BehaviorSubject<User | undefined>(undefined);
  urlBeforeLogin?: string;

  async canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Promise<boolean|UrlTree> {
    const user = await this.getUser();
    if (!user) {
      //this.urlBeforeLogin = route.url;
      return this.router.parseUrl('/login');
    } else {
      return true;
    }
  }

  private token?: string;

  constructor(
    private storage: Storage,
    private router: Router,
    private sharedDataService: SharedDataService,
  ) {
  }

  async init(): Promise<void> {
    await this.storage.create().then();
  }

  get isLoggedIn(): boolean {
    return !!this.user$.getValue();
  }

  async getUser(): Promise<User | undefined> {
    await this.storage.create();
    if (!this.user$.getValue()) {
      const data = await this.storage.get('user');
      if (data) {
        this.user$.next(new User(data));
      }
    }
    return this.user$.getValue();
  }

  async setUser(value: User | undefined) {
    this.user$.next(value);
    await this.storage.set('user', value).then();
    
    // Also update SharedDataService cache if user exists
    if (value) {
      this.sharedDataService.storeUser(value);
    }
  }

  /**
   * Updates user in both WebAuthenticationService and SharedDataService caches,
   * and persists to storage. Use this method when you need to update the current user.
   */
  async updateUser(user: User): Promise<User> {
    // Update in SharedDataService (which also updates database)
    const updatedUser = await this.sharedDataService.updateUser(user);
    
    // Update in WebAuthenticationService cache
    await this.setUser(updatedUser);
    
    return updatedUser;
  }

  async getToken(): Promise<string | undefined> {
    return this.token ?? ( this.token = (await this.storage.get('token')) as any as string);
  }

  async setToken(value: string | undefined) {
    this.token = value;
    await this.storage.set('token', value).then();
  }

  async logout() {
    console.log('WebAuthenticationService: Starting logout process');
    // Clear cache and user data
    this.sharedDataService.clear();
    console.log('WebAuthenticationService: Cache cleared');
    await this.setToken(undefined);
    await this.setUser(undefined);
    console.log('WebAuthenticationService: User and token cleared');
    await this.router.navigateByUrl('login');
    console.log('WebAuthenticationService: Navigated to login');
  }

  public async hasAdminAccess(): Promise<boolean> {
    return await new BaseSecurityUserHasPermissionRequest({
      permission: userPermissions.accessAdminBackend,
      userId: (await this.getUser()).id
    }).execute();
  }

  public async hasCreatorAccess(): Promise<boolean> {
    return await new BaseSecurityUserHasPermissionRequest({
      permission: userPermissions.accessCreatorBackend,
      userId: (await this.getUser()).id
    }).execute();
  }
}
